// src/services/tallyExportService.js
// Generates Tally ERP 9 compatible XML for import
// Supports: Sales Vouchers (Invoices), Payment Vouchers (Expenses), Purchase Vouchers (POs)

// ─── GST determination ───────────────────────────────────
// If company state_code === client state_code → CGST + SGST (intra-state)
// Else → IGST (inter-state)
// If state unknown → default to CGST + SGST

const isInterState = (companyStateCode, clientStateCode) => {
  if (!companyStateCode || !clientStateCode) return false; // default intra
  return companyStateCode !== clientStateCode;
};

// ─── Date format: YYYYMMDD ────────────────────────────────
const tallyDate = (dateInput) => {
  if (!dateInput) return "";
  let d;
  if (dateInput?.seconds) d = new Date(dateInput.seconds * 1000);
  else if (dateInput instanceof Date) d = dateInput;
  else d = new Date(dateInput);
  if (isNaN(d)) return "";
  const y  = d.getFullYear();
  const m  = String(d.getMonth() + 1).padStart(2, "0");
  const dy = String(d.getDate()).padStart(2, "0");
  return `${y}${m}${dy}`;
};

// ─── Escape XML special chars ─────────────────────────────
const esc = (str) =>
  String(str || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

// ─── Tally XML envelope wrapper ───────────────────────────
const wrapEnvelope = (vouchersXml) => `<?xml version="1.0" encoding="UTF-8"?>
<ENVELOPE>
  <HEADER>
    <TALLYREQUEST>Import Data</TALLYREQUEST>
  </HEADER>
  <BODY>
    <IMPORTDATA>
      <REQUESTDESC>
        <REPORTNAME>Vouchers</REPORTNAME>
        <STATICVARIABLES>
          <SVCURRENTCOMPANY>##SVCURRENTCOMPANY</SVCURRENTCOMPANY>
        </STATICVARIABLES>
      </REQUESTDESC>
      <REQUESTDATA>
        ${vouchersXml}
      </REQUESTDATA>
    </IMPORTDATA>
  </BODY>
</ENVELOPE>`;

// ═══════════════════════════════════════════════════════════
// SALES VOUCHER — from Invoice
// ═══════════════════════════════════════════════════════════
const buildSalesVoucher = (invoice, company, config) => {
  const date         = tallyDate(invoice.invoice_date || invoice.created_at);
  const clientName   = esc(invoice.client_name || "Unknown Party");
  const invoiceNo    = esc(invoice.invoice_no  || "");
  const narration    = esc(`Invoice ${invoiceNo} - ${clientName}`);
  const totalAmount  = Number(invoice.total_amount  || 0);
  const subtotal     = Number(invoice.subtotal       || totalAmount);
  const discount     = Number(invoice.discount       || 0);
  const taxableAmt   = subtotal - discount;
  const taxAmount    = Number(invoice.tax_amount     || 0);

  // GST split
  const interState   = isInterState(company.state_code, invoice.client_state_code);
  const salesLedger  = esc(config.tally_sales_ledger  || "Service Revenue");
  const cgstLedger   = esc(config.tally_cgst_ledger   || "Output CGST");
  const sgstLedger   = esc(config.tally_sgst_ledger   || "Output SGST");
  const igstLedger   = esc(config.tally_igst_ledger   || "Output IGST");

  // GST entries
  let gstEntries = "";
  if (taxAmount > 0) {
    if (interState) {
      gstEntries = `
        <ALLLEDGERENTRIES.LIST>
          <LEDGERNAME>${igstLedger}</LEDGERNAME>
          <ISDEEMEDPOSITIVE>No</ISDEEMEDPOSITIVE>
          <AMOUNT>${taxAmount.toFixed(2)}</AMOUNT>
        </ALLLEDGERENTRIES.LIST>`;
    } else {
      const half = (taxAmount / 2).toFixed(2);
      gstEntries = `
        <ALLLEDGERENTRIES.LIST>
          <LEDGERNAME>${cgstLedger}</LEDGERNAME>
          <ISDEEMEDPOSITIVE>No</ISDEEMEDPOSITIVE>
          <AMOUNT>${half}</AMOUNT>
        </ALLLEDGERENTRIES.LIST>
        <ALLLEDGERENTRIES.LIST>
          <LEDGERNAME>${sgstLedger}</LEDGERNAME>
          <ISDEEMEDPOSITIVE>No</ISDEEMEDPOSITIVE>
          <AMOUNT>${half}</AMOUNT>
        </ALLLEDGERENTRIES.LIST>`;
    }
  }

  return `
    <TALLYMESSAGE xmlns:UDF="TallyUDF">
      <VOUCHER REMOTEID="${esc(invoice.id)}" VCHTYPE="Sales" ACTION="Create" OBJVIEW="Invoice Voucher View">
        <DATE>${date}</DATE>
        <REFERNCENO>${invoiceNo}</REFERNCENO>
        <VOUCHERTYPENAME>Sales</VOUCHERTYPENAME>
        <PARTYLEDGERNAME>${clientName}</PARTYLEDGERNAME>
        <NARRATION>${narration}</NARRATION>
        <PERSISTEDVIEW>Invoice Voucher View</PERSISTEDVIEW>
        <ALLLEDGERENTRIES.LIST>
          <LEDGERNAME>${clientName}</LEDGERNAME>
          <ISDEEMEDPOSITIVE>Yes</ISDEEMEDPOSITIVE>
          <AMOUNT>-${totalAmount.toFixed(2)}</AMOUNT>
        </ALLLEDGERENTRIES.LIST>
        <ALLLEDGERENTRIES.LIST>
          <LEDGERNAME>${salesLedger}</LEDGERNAME>
          <ISDEEMEDPOSITIVE>No</ISDEEMEDPOSITIVE>
          <AMOUNT>${taxableAmt.toFixed(2)}</AMOUNT>
        </ALLLEDGERENTRIES.LIST>
        ${gstEntries}
      </VOUCHER>
    </TALLYMESSAGE>`;
};

// ═══════════════════════════════════════════════════════════
// PAYMENT VOUCHER — from Expense / Transport
// ═══════════════════════════════════════════════════════════
const buildPaymentVoucher = (expense, company, config) => {
  const date          = tallyDate(expense.created_at);
  const amount        = Number(expense.amount || 0);
  const description   = esc(expense.description || expense.category || "Expense");
  const narration     = esc(description);
  const expLedger     = esc(config.tally_expense_ledger || "Indirect Expenses");
  const mode          = (expense.payment_mode || "cash").toLowerCase();

  // Payment mode → ledger
  let paymentLedger;
  if (mode === "cash") {
    paymentLedger = esc(config.tally_cash_ledger || "Cash");
  } else if (mode === "bank_transfer" || mode === "bank") {
    paymentLedger = esc(config.tally_bank_ledger || "Bank Account");
  } else if (mode === "upi") {
    paymentLedger = esc(config.tally_bank_ledger || "Bank Account"); // UPI = bank
  } else {
    paymentLedger = esc(config.tally_cash_ledger || "Cash");
  }

  return `
    <TALLYMESSAGE xmlns:UDF="TallyUDF">
      <VOUCHER REMOTEID="${esc(expense.id)}" VCHTYPE="Payment" ACTION="Create" OBJVIEW="Accounting Voucher View">
        <DATE>${date}</DATE>
        <VOUCHERTYPENAME>Payment</VOUCHERTYPENAME>
        <NARRATION>${narration}</NARRATION>
        <ALLLEDGERENTRIES.LIST>
          <LEDGERNAME>${expLedger}</LEDGERNAME>
          <ISDEEMEDPOSITIVE>Yes</ISDEEMEDPOSITIVE>
          <AMOUNT>-${amount.toFixed(2)}</AMOUNT>
        </ALLLEDGERENTRIES.LIST>
        <ALLLEDGERENTRIES.LIST>
          <LEDGERNAME>${paymentLedger}</LEDGERNAME>
          <ISDEEMEDPOSITIVE>No</ISDEEMEDPOSITIVE>
          <AMOUNT>${amount.toFixed(2)}</AMOUNT>
        </ALLLEDGERENTRIES.LIST>
      </VOUCHER>
    </TALLYMESSAGE>`;
};

// ═══════════════════════════════════════════════════════════
// PURCHASE VOUCHER — from PO / Vendor Payment
// ═══════════════════════════════════════════════════════════
const buildPurchaseVoucher = (po, company, config) => {
  const date           = tallyDate(po.created_at || po.grn_completed_at);
  const vendorName     = esc(po.vendor_name || "Unknown Vendor");
  const poNo           = esc(po.po_number   || po.reference_no || "");
  const narration      = esc(`PO ${poNo} - ${vendorName}`);
  const totalAmount    = Number(po.grand_total || po.total_amount || po.amount || 0);
  const purchaseLedger = esc(config.tally_purchase_ledger || "Purchases");

  // Input GST — assume 18% if tax info present
  const taxPercent   = Number(po.tax_percent || 0);
  const taxAmount    = taxPercent > 0
    ? (totalAmount - totalAmount / (1 + taxPercent / 100))
    : 0;
  const taxableAmt   = totalAmount - taxAmount;

  const interState   = isInterState(company.state_code, po.vendor_state_code);
  const inputCGST    = esc(config.tally_input_cgst || "Input CGST");
  const inputSGST    = esc(config.tally_input_sgst || "Input SGST");
  const inputIGST    = esc(config.tally_input_igst || "Input IGST");

  let gstEntries = "";
  if (taxAmount > 0) {
    if (interState) {
      gstEntries = `
        <ALLLEDGERENTRIES.LIST>
          <LEDGERNAME>${inputIGST}</LEDGERNAME>
          <ISDEEMEDPOSITIVE>Yes</ISDEEMEDPOSITIVE>
          <AMOUNT>-${taxAmount.toFixed(2)}</AMOUNT>
        </ALLLEDGERENTRIES.LIST>`;
    } else {
      const half = (taxAmount / 2).toFixed(2);
      gstEntries = `
        <ALLLEDGERENTRIES.LIST>
          <LEDGERNAME>${inputCGST}</LEDGERNAME>
          <ISDEEMEDPOSITIVE>Yes</ISDEEMEDPOSITIVE>
          <AMOUNT>-${half}</AMOUNT>
        </ALLLEDGERENTRIES.LIST>
        <ALLLEDGERENTRIES.LIST>
          <LEDGERNAME>${inputSGST}</LEDGERNAME>
          <ISDEEMEDPOSITIVE>Yes</ISDEEMEDPOSITIVE>
          <AMOUNT>-${half}</AMOUNT>
        </ALLLEDGERENTRIES.LIST>`;
    }
  }

  return `
    <TALLYMESSAGE xmlns:UDF="TallyUDF">
      <VOUCHER REMOTEID="${esc(po.id)}" VCHTYPE="Purchase" ACTION="Create" OBJVIEW="Accounting Voucher View">
        <DATE>${date}</DATE>
        <REFERNCENO>${poNo}</REFERNCENO>
        <VOUCHERTYPENAME>Purchase</VOUCHERTYPENAME>
        <PARTYLEDGERNAME>${vendorName}</PARTYLEDGERNAME>
        <NARRATION>${narration}</NARRATION>
        <ALLLEDGERENTRIES.LIST>
          <LEDGERNAME>${vendorName}</LEDGERNAME>
          <ISDEEMEDPOSITIVE>No</ISDEEMEDPOSITIVE>
          <AMOUNT>${totalAmount.toFixed(2)}</AMOUNT>
        </ALLLEDGERENTRIES.LIST>
        <ALLLEDGERENTRIES.LIST>
          <LEDGERNAME>${purchaseLedger}</LEDGERNAME>
          <ISDEEMEDPOSITIVE>Yes</ISDEEMEDPOSITIVE>
          <AMOUNT>-${taxableAmt.toFixed(2)}</AMOUNT>
        </ALLLEDGERENTRIES.LIST>
        ${gstEntries}
      </VOUCHER>
    </TALLYMESSAGE>`;
};

// ═══════════════════════════════════════════════════════════
// MAIN EXPORT FUNCTIONS
// ═══════════════════════════════════════════════════════════

export const generateSalesXML = (invoices, company, config) => {
  const vouchers = invoices
    .filter(inv => Number(inv.total_amount || 0) > 0)
    .map(inv => buildSalesVoucher(inv, company, config))
    .join("\n");
  return wrapEnvelope(vouchers);
};

export const generateExpenseXML = (expenses, company, config) => {
  const vouchers = expenses
    .filter(exp => Number(exp.amount || 0) > 0)
    .map(exp => buildPaymentVoucher(exp, company, config))
    .join("\n");
  return wrapEnvelope(vouchers);
};

export const generatePurchaseXML = (pos, company, config) => {
  const vouchers = pos
    .filter(po => Number(po.grand_total || po.total_amount || po.amount || 0) > 0)
    .map(po => buildPurchaseVoucher(po, company, config))
    .join("\n");
  return wrapEnvelope(vouchers);
};

export const generateAllXML = (invoices, expenses, pos, company, config) => {
  const salesV    = invoices
    .filter(inv => Number(inv.total_amount || 0) > 0)
    .map(inv => buildSalesVoucher(inv, company, config));
  const expenseV  = expenses
    .filter(exp => Number(exp.amount || 0) > 0)
    .map(exp => buildPaymentVoucher(exp, company, config));
  const purchaseV = pos
    .filter(po => Number(po.grand_total || po.total_amount || po.amount || 0) > 0)
    .map(po => buildPurchaseVoucher(po, company, config));
  return wrapEnvelope([...salesV, ...expenseV, ...purchaseV].join("\n"));
};

// ─── Download helper ─────────────────────────────────────
export const downloadXML = (xmlContent, filename) => {
  const blob = new Blob([xmlContent], { type: "text/xml;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href  = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
